import { useState } from "react";
import { useMutation } from "@apollo/client";
import { REQUEST_ACCESS_MUTATION } from "../../../graphql/mutations";

interface PricingRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  planCode: string;
  planName: string;
}

const PricingRequestModal = ({
  isOpen,
  onClose,
  planCode,
  planName,
}: PricingRequestModalProps) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState<{
    success?: boolean;
    message?: string;
  } | null>(null);

  const [requestAccess] = useMutation(REQUEST_ACCESS_MUTATION, {
    onCompleted: (data) => {
      setIsSubmitting(false);
      if (data.requestAccess.success) {
        setFormStatus({
          success: true,
          message: "Thank you! We'll be in touch soon about your access.",
        });
        setEmail("");
        setName("");
      } else {
        setFormStatus({
          success: false,
          message:
            data.requestAccess.errors?.[0] ||
            "Something went wrong. Please try again.",
        });
      }
    },
    onError: (error) => {
      setIsSubmitting(false);
      setFormStatus({
        success: false,
        message: error.message || "Something went wrong. Please try again.",
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    setFormStatus(null);

    await requestAccess({
      variables: {
        email: email.trim(),
        name: name.trim() || null,
        planCode,
        source: "pricing_card",
      },
    });
  };

  const handleClose = () => {
    setFormStatus(null);
    setEmail("");
    setName("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-900 border border-gray-200/80 dark:border-gray-700/60 rounded-md shadow-2xl max-w-md w-full p-8">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="mb-6">
          <p className="text-xs font-light tracking-[0.3em] uppercase text-gray-400 dark:text-gray-500 mb-2">
            Request Access
          </p>
          <h3 className="text-2xl font-light tracking-wide text-gray-900 dark:text-white">
            {planName}{" "}
            <span className="text-sm font-light text-gray-500">Plan</span>
          </h3>
        </div>

        {formStatus?.success ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-sm font-light text-green-600 dark:text-green-400">
              {formStatus.message}
            </p>
            <button
              onClick={handleClose}
              className="mt-6 text-xs tracking-[0.2em] uppercase font-light text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-light tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                Full Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full px-4 py-3 bg-white/80 dark:bg-gray-800/80 border border-gray-200/50 dark:border-gray-700/50 text-gray-900 dark:text-white text-sm font-light tracking-wide placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-pink-400 focus:ring-2 focus:ring-pink-200/50 dark:focus:ring-pink-800/50 transition-all duration-300 rounded-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-light tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                Email Address <span className="text-pink-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full px-4 py-3 bg-white/80 dark:bg-gray-800/80 border border-gray-200/50 dark:border-gray-700/50 text-gray-900 dark:text-white text-sm font-light tracking-wide placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-pink-400 focus:ring-2 focus:ring-pink-200/50 dark:focus:ring-pink-800/50 transition-all duration-300 rounded-sm"
              />
            </div>

            {formStatus && !formStatus.success && (
              <p className="text-xs font-light text-red-600 dark:text-red-400">
                {formStatus.message}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-2 bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-200 hover:from-gray-800 hover:to-gray-600 dark:hover:from-gray-100 dark:hover:to-gray-300 text-white dark:text-gray-900 font-light px-6 py-3 text-sm tracking-[0.2em] uppercase transition-all duration-300 rounded-sm disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Submitting..." : "Request Access"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default PricingRequestModal;
